import { index, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { usStatesEnum, usStatesEnumSchema } from "./commonSchema";
import { customTimestamp } from "./utils/types";

export const healthPlansTable = pgTable(
  "health_plans",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: text("name").notNull(),
    usState: usStatesEnum("us_state").notNull(),

    // 5-digit county FIPS codes (2-digit state + 3-digit county), stored as
    // strings to preserve leading zeros. Null for statewide/uniform-model plans;
    // populated for county-based states so the codes match the ZIP→FIPS lookup.
    countyFipsCodes: text("county_fips_codes").array(),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("health_plans_us_state_index").on(table.usState),
    // GIN index supports "which plans cover county X" array-membership lookups.
    index("health_plans_county_fips_codes_index").using(
      "gin",
      table.countyFipsCodes,
    ),
  ],
);

const countyFipsCodeSchema = z.string().regex(/^\d{5}$/);

export const healthPlanSchema = createSelectSchema(healthPlansTable, {
  usState: usStatesEnumSchema,
  countyFipsCodes: z.array(countyFipsCodeSchema).nullable(),
});

export type HealthPlanType = z.infer<typeof healthPlanSchema>;

export const insertHealthPlanSchema = createInsertSchema(healthPlansTable, {
  name: z.string().trim().min(1),
  usState: usStatesEnumSchema,
  countyFipsCodes: z.array(countyFipsCodeSchema).nullable().optional(),
}).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertHealthPlanType = z.infer<typeof insertHealthPlanSchema>;
